// Node Module Imports
import React from 'react'

import { graphql, Link } from 'gatsby'
import styled from 'styled-components'

// Component Imports
import UmaMissionBg from '../../assets/images/about/uma-mission.jpg'
import Breadcrumbs from '../../components/core/Breadcrumbs'
import Layout from '../../components/core/layout'
import SEO from '../../components/core/seo'
import Accredited from '../../components/cross-template-statics/AccreditedYouBet'
import ChevronBackgroundStroke from '../../components/elements/ChevronBackgroundStroke'
import ChevronBackgroundTwoTone from '../../components/elements/ChevronBackgroundTwoTone'
import HeroInternal from '../../components/elements/HeroInternal'
import TwoColWithBorder from '../../components/elements/TwoColWithBorder'
import ChevronBackground from '../../components/reusables/ChevronBackground'
import TextBoxOverImage from '../../components/reusables/TextBoxOverImage'
import VideoLazyload from '../../components/reusables/VideoLazyload'
import YouTubeEmbed from '../../components/reusables/YouTubeEmbed'
import Values from '../../components/specific-to-template/about/Values'
// Style Imports
import { above, colors, space } from '../../style/design-system'
// other
import { studentInfo } from '../../utils/static-data/studentInfo'

const AboutPage = ({ className, data, pageContext }) => (
  <Layout>
    <SEO
      title="About UMA"
      description="We help students transform their lives through supportive career training in allied healthcare. Learn more about Ultimate Medical Academy now."
    />

    <div className={className}>
      <HeroInternal
        className="hero"
        backgroundImage={{
          image: data.heroImgLg.childImageSharp.gatsbyImageData,
          objectPosition: '50% 50%',
        }}
        contentLg="About UMA"
        contentSm="Supporting our students in healthcare education since 1994."
      />
      <main>
        <div className="container text-lg-center">
          <Breadcrumbs pageContext={pageContext} />
        </div>

        <div className="container d-lg-none">
          <h1 className="mt-0">About UMA</h1>
          <p className="mb-0">Supporting our students in healthcare education since 1994.</p>
        </div>

        <div className="container pb-9 py-lg-10">
          <div className="row">
            <div className="col-lg-6 order-lg-2 my-8 my-lg-0">
              <VideoLazyload
                displayPlayIcon
                posterGatsbyImg={{ image: data.videoPoster.childImageSharp.gatsbyImageData }}
                posterAlt="UMA">
                <YouTubeEmbed autoplay title="UMA" videoId="GzZWqTUuR68" />
              </VideoLazyload>
            </div>
            <div className="col-lg-6 order-lg-1">
              <h2 className="mt-0 blue">Our nonprofit story continues.</h2>
              <p>
                Ultimate Medical Academy is a nonprofit healthcare educational institution with a national presence. For
                over {studentInfo.yearsInBusiness} years, we’ve offered allied healthcare education, and now we offer
                content-rich, interactive online courses to approximately {studentInfo.currentStudents.number} students
                as well as campus-based training to hundreds of students. With more than {studentInfo.grads.number}{' '}
                alumni, we are institutionally accredited by the Accrediting Bureau of Health Education Schools (
                <a href="https://abhes.org" rel="noopener noreferrer" target="_blank">
                  abhes.org
                </a>
                ).
              </p>
              <p>
                To learn more about the healthcare programs offered at UMA, head to our{' '}
                <a href="/campuses/online/">online campus</a> and <a href="/campuses/clearwater/">ground campus</a>{' '}
                pages.
              </p>
            </div>
          </div>
        </div>

        <ChevronBackground backgroundColor="blue" direction="north" className="py-9 py-lg-10">
          <div className="container text-lg-center">
            <div className="row">
              <div className="col-lg-10 offset-lg-1">
                <h2 className="mt-0 text-white">Here for our students, every step of the way</h2>
                <p className="text-white">
                  With a mission to equip and empower students to excel in healthcare careers, the UMA experience
                  includes individualized student services throughout all phases of the student journey, from admissions
                  to assistance with the job search.
                </p>
                <p className="text-white">
                  Our students have access to academic advising, one-on-one academic support, resume and interview
                  coaching, job search assistance, technical support, and more. We support our alumni in finding new
                  jobs, progressing in their current positions, and remaining competitive in the job market.
                </p>
                <p>
                  <Link to="/future-students/student-services/" className="btn btn-outline-white">
                    Student Services&nbsp;&gt;
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </ChevronBackground>

        <TwoColWithBorder
          className="py-9 py-lg-10"
          borderAt={6}
          content1={() => (
            <>
              <h2 className="mt-0 blue">Valuable employer partnerships</h2>
              <p>
                We are proud to offer allied healthcare staffing solutions — in the form of skilled UMA healthcare grads
                — to our employer partners across the country.
              </p>
              <p>
                <Link to="/employers" className="btn btn-outline-black">
                  Employer Partners&nbsp;&gt;
                </Link>
              </p>
            </>
          )}
          content2={() => (
            <>
              <h2 className="mt-0 blue">Continuing medical education</h2>
              <p>
                We also provide Continuing Medical Education (CME) offerings that provide ongoing training and
                professional development opportunities to physicians, nurses, and other medical professionals throughout
                the U.S. annually.
              </p>
              <p>
                <Link to="/about/continuing-medical-education/" className="btn btn-outline-black">
                  Learn More&nbsp;&gt;
                </Link>
              </p>
            </>
          )}
        />

        <TextBoxOverImage
          gatsbyImage={{
            image: data.cultureImg.childImageSharp.gatsbyImageData,
          }}
          gatsbyImageMobile={{
            image: data.cultureImg.childImageSharp.gatsbyImageData,
          }}
          bootstrapClasses="col-lg-6 offset-lg-3"
          childBootstrapClasses="col-12 col-xl-8 offset-xl-2"
          className="py-lg-12">
          <h2 className="mt-0 text-white">Our Culture</h2>
          <p className="text-white">
            If you like helping others, you’re in good company here. UMA students and team members are not only
            passionate about healthcare, but also about making a positive impact on the community around them through
            community service and philanthropic activities that we support throughout the year. We respect a healthy
            balance of work life and play time. Learn more about what it’s like to work here.
          </p>
          <p className="mb-0">
            <a href="https://workatuma.com" target="_blank" className="btn btn-outline-white">
              Work Here&nbsp;&gt;
            </a>
          </p>
        </TextBoxOverImage>

        <ChevronBackgroundStroke direction="east" className="py-9 py-lg-10">
          <div className="container">
            <h2 className="mt-0 blue">Our History</h2>

            <div className="timeline position-relative py-8">
              <div className="tl-item">
                <h3 className="mt-0">1994</h3>
                <p>
                  After being founded in Clearwater, Florida as Ultimate Learning Center, Inc., the institution was
                  recognized by Florida’s Commission on Independent Education and started serving the Tampa Bay
                  community’s need for trained nursing assistants and related roles. Over time, we recognized the
                  growing need for trained professionals in other healthcare specialties and employers’ needs for
                  multi-skilled staff, so the institution began to offer a wider range of programs focused on developing
                  allied healthcare professionals.
                </p>
              </div>
              <div className="tl-item">
                <h3 className="mt-0">2009</h3>
                <p>
                  In 2009, now operating as Ultimate Medical Academy, we launched online allied healthcare courses to
                  students throughout the U.S. Following the launch of its online programs, UMA later added a continuing
                  medical education (CME) division that creates innovative programming to maximize the learner
                  experience and, ultimately, improve patient outcomes.
                </p>
              </div>
              <div className="tl-item">
                <h3 className="mt-0">2015</h3>
                <p>
                  Clinical and Patient Educators Association (CPEA), a 501(c)(3) nonprofit organization, formed UMA
                  Education, Inc. and acquired UMA. CPEA focuses on developing quality, innovative education for
                  healthcare practitioners and the patient communities they serve.
                </p>
              </div>
            </div>
          </div>
        </ChevronBackgroundStroke>

        <ChevronBackgroundTwoTone
          chevron={{ display: false }}
          backgroundColor={colors.dev.green.d()}
          className="py-9 py-lg-10 text-white-force text-lg-center">
          <div className="container">
            <div className="row">
              <div className="col-lg-10 offset-lg-1">
                <h2 className="mt-0">Governance</h2>
                <p>
                  UMA is led by Thomas Rametta, President, and governed by a board of trustees comprised of individuals
                  with significant experience in education, medicine, nonprofits, law, information technology, and
                  finance. The Board includes Chair Darlyne Bailey, Ph.D.; Stephen Burghardt, Ph.D.; Terence L. Byrd;
                  Hugh Campbell; Shelley Collins, M.D., FAAP; Christopher Hawk, M.D.; Karen M. Jones, Sheila McDevitt;
                  Karen Mincey; Thomas Rametta; and Vice Chair Saundra Wall Williams, Ed.D.
                </p>
              </div>
            </div>
          </div>
        </ChevronBackgroundTwoTone>

        <div className="section-mission position-relative">
          <div className="lazy bg-img" data-bg={UmaMissionBg} />
          <div className="container">
            <div className="row">
              <div className="col-md-5">
                <div className="top py-9 py-lg-10">
                  <h2 className="mt-0 blue">Our Mission</h2>
                  <p>
                    Ultimate Medical Academy is a dynamic educational institution committed to equipping and empowering
                    students to excel in healthcare careers. We strive to provide a learning experience that maximizes
                    value for our students in a professional, supportive and ethical environment.
                  </p>
                </div>
                <div className="py-9 py-lg-10">
                  <h2 className="mt-0 blue">Diversity and Inclusion</h2>
                  <p>
                    We recognize and value distinct life experiences and perspectives. Ultimate Medical Academy seeks to
                    inspire our team members, and in turn our students, by creating a culture where diversity,
                    inclusion, and respect for others are championed. Learn more about how we embrace diversity at UMA.
                  </p>
                  <p className="mb-0">
                    <Link to="/about/diversity-and-inclusion" className="btn btn-outline-black">
                      Learn More&nbsp;&gt;
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Values />

        <Accredited />
      </main>
    </div>
  </Layout>
)

export default styled(AboutPage)`
  .timeline {
    display: flex;
    flex-wrap: wrap;

    .tl-item {
      border-left: 2px dashed ${colors.dev.green.e()};
      flex: 1 1 33%;

      h3 {
        border-bottom: 3px solid ${colors.dev.green.e()};
      }

      * {
        padding: 0 ${space.f};
      }
    }
  }

  .top {
    border-bottom: 1px solid ${colors.dev.gray.b()};
  }

  .hero .gatsby-image-wrapper {
    ${above.lg`
      width: 83% !important;
    `}
  }

  .section-mission {
    .bg-img {
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      min-height: 300px;

      ${above.md`
        background-position: center bottom;
        position: absolute;
        top: 0; bottom: 0; right: 0; left: 50%;
        width: 50%;
      }`}
    }
  }
`

export const query = graphql`
  {
    heroImgLg: file(absolutePath: { regex: "/images/about/hero.jpg/" }) {
      childImageSharp {
        gatsbyImageData(width: 1180, quality: 85, placeholder: NONE, layout: FIXED)
      }
    }
    cultureImg: file(absolutePath: { regex: "/about/UMA-culture-bg.jpg/" }) {
      childImageSharp {
        gatsbyImageData(quality: 85, placeholder: NONE, layout: FULL_WIDTH)
      }
    }
    videoPoster: file(absolutePath: { regex: "/about/about-uma-video-poster.jpg/" }) {
      childImageSharp {
        gatsbyImageData(width: 700, quality: 85, placeholder: NONE, layout: CONSTRAINED)
      }
    }
  }
`
